/**
 * "Change dates" — RP-28. Two actions, because "redistribute" means two different things:
 *
 *   • Move           — the same plan, later (or earlier): every month's hours slide.
 *   • Fit to new dates — the same work in a different window. The plan's Budget Cadence
 *                      decides the rule (Total keeps each line's total; Monthly keeps the
 *                      run-rate), so the planner never has to pick one.
 *
 * The dialog previews before it writes: three numbers (hours, months, margin), any reason
 * the change can't be made, and the per-line diff on demand. Apply sends the same request;
 * the server recomputes rather than trusting the preview. Bill rates never change here.
 */

import { AlertTriangle, ChevronDown, ChevronRight, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MonthSelect } from "@/components/shared/MonthSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type PlanDatesRequest,
  useApplyPlanDates,
  usePlanDatesPreview,
} from "@/hooks/use-plan-dates";
import { endMonthOptions, startMonthOptions } from "@/lib/month-options";
import {
  formatPlanHours,
  formatPlanMoney,
  formatPlanMonth,
  formatPlanPercent,
} from "@/lib/plan-format";
import { shiftMonth } from "@/lib/plan-window";
import type { PlanDatesPreviewView } from "@/schemas/plan-dates";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
/** `2027-03` → `Mar 2027`. */
export const longMonth = formatPlanMonth;

function range(start: string, end: string | null): string {
  return `${longMonth(start)} – ${end === null ? "Planning Horizon" : longMonth(end)}`;
}

interface Props {
  plan: ResourcePlanDetailView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PlanDatesDialog({ plan, open, onOpenChange }: Props) {
  const [mode, setMode] = useState<"Move" | "Fit">("Move");
  const [startMonth, setStartMonth] = useState(plan.startMonth);
  const [endMonth, setEndMonth] = useState(plan.endMonth ?? "");
  const [reason, setReason] = useState("");
  const [showLines, setShowLines] = useState(false);
  const apply = useApplyPlanDates(plan.id);

  // Start from the plan's current dates every time the dialog OPENS — and at no other
  // time: the plan refetches the moment a change applies, and resetting on that would
  // redraw the form under the planner's last click. Read through a ref for that reason.
  const latestDates = useRef({ start: plan.startMonth, end: plan.endMonth });
  latestDates.current = { start: plan.startMonth, end: plan.endMonth };
  useEffect(() => {
    if (!open) return;
    setMode("Move");
    setStartMonth(latestDates.current.start);
    setEndMonth(latestDates.current.end ?? "");
    setReason("");
    setShowLines(false);
  }, [open]);

  const total = plan.budgetCadence === "Total";
  const validStart = MONTH.test(startMonth);
  const validEnd = endMonth === "" ? !total : MONTH.test(endMonth) && endMonth >= startMonth;
  const unchanged =
    mode === "Move"
      ? startMonth === plan.startMonth
      : startMonth === plan.startMonth && endMonth === (plan.endMonth ?? "");

  const request: PlanDatesRequest | null =
    !open || !validStart || unchanged || (mode === "Fit" && !validEnd)
      ? null
      : mode === "Move"
        ? { mode, startMonth }
        : { mode, startMonth, endMonth: endMonth === "" ? null : endMonth };
  const debounced = useDebouncedValue(request, 250);
  const preview = usePlanDatesPreview(plan.id, plan.updatedAt, debounced);
  const view = preview.data;
  const current = view !== undefined && debounced !== null && request !== null;

  // A Move keeps the plan's length, so its end follows the start.
  const movedEnd =
    plan.endMonth === null || !validStart
      ? null
      : shiftMonth(plan.endMonth, monthsBetween(plan.startMonth, startMonth));

  const reasonMissing = view?.reasonRequired === true && reason.trim() === "";
  const canApply =
    current && view.blocked === null && !reasonMissing && !apply.isPending && !preview.isFetching;

  function submit() {
    if (request === null) return;
    apply
      .mutateAsync({ ...request, ...(reason.trim() === "" ? {} : { reason: reason.trim() }) })
      .then(() => onOpenChange(false))
      // The hook's onError has already shown the toast; the dialog stays open to retry.
      .catch(() => undefined);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Change dates</DialogTitle>
          <DialogDescription>
            Currently {range(plan.startMonth, plan.endMonth)}. Rates don&rsquo;t change; you see the
            result before anything is saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset className="grid grid-cols-2 gap-2">
            <legend className="sr-only">How the dates change</legend>
            <ModeOption
              selected={mode === "Move"}
              onSelect={() => setMode("Move")}
              title="Move the plan"
              detail="Every month's hours slide to the new start."
            />
            <ModeOption
              selected={mode === "Fit"}
              onSelect={() => setMode("Fit")}
              title="Fit to new dates"
              detail={
                total
                  ? "Each line keeps its total hours, spread over the new months."
                  : "Each line keeps its monthly hours; added months copy the nearest month."
              }
            />
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="plan-dates-start">Start month</Label>
              <MonthSelect
                id="plan-dates-start"
                value={startMonth}
                options={startMonthOptions(startMonth)}
                onValueChange={(next) => {
                  setStartMonth(next);
                  // The End list counts from the Start; an End now before it is no answer.
                  if (endMonth !== "" && endMonth < next) setEndMonth("");
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-dates-end">
                End month{" "}
                {mode === "Fit" && !total && (
                  <span className="font-normal text-muted-foreground">(optional)</span>
                )}
              </Label>
              {mode === "Move" ? (
                <p
                  id="plan-dates-end"
                  className="flex h-9 items-center text-sm text-muted-foreground"
                >
                  {plan.endMonth === null
                    ? "Planning Horizon"
                    : movedEnd === null
                      ? "—"
                      : `${longMonth(movedEnd)} (follows the start)`}
                </p>
              ) : (
                <MonthSelect
                  id="plan-dates-end"
                  value={endMonth}
                  options={endMonthOptions(startMonth, endMonth)}
                  onValueChange={setEndMonth}
                  {...(total ? {} : { noneLabel: "Planning Horizon" })}
                />
              )}
            </div>
          </div>
          {mode === "Fit" && !validEnd && validStart && (
            <p className="text-sm text-destructive">
              {total && endMonth === ""
                ? "A Total-budget plan needs an end month."
                : "The end month can't be before the start month."}
            </p>
          )}

          {current ? (
            <PreviewBody
              view={view}
              showLines={showLines}
              onToggleLines={() => setShowLines((v) => !v)}
            />
          ) : preview.isError && request !== null ? (
            <p className="text-sm text-destructive">{preview.error.message}</p>
          ) : request !== null ? (
            <p className="text-sm text-muted-foreground">Working out the new dates…</p>
          ) : null}

          {view?.reasonRequired === true && current && view.blocked === null && (
            <div className="space-y-1.5">
              <Label htmlFor="plan-dates-reason">Why are the dates changing?</Label>
              <Textarea
                id="plan-dates-reason"
                rows={2}
                maxLength={1000}
                placeholder="e.g. Client pushed the kickoff to March"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canApply}>
            {apply.isPending ? "Changing…" : mode === "Move" ? "Move plan" : "Fit to new dates"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function monthsBetween(from: string, to: string): number {
  return (
    (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 +
    (Number(to.slice(5, 7)) - Number(from.slice(5, 7)))
  );
}

function ModeOption(props: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={props.selected}
      onClick={props.onSelect}
      className={`rounded-md border p-3 text-left transition-colors ${
        props.selected
          ? "border-primary bg-brand-50 text-foreground"
          : "border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      <span className="block text-sm font-semibold">{props.title}</span>
      <span className="mt-0.5 block text-xs">{props.detail}</span>
    </button>
  );
}

function PreviewBody({
  view,
  showLines,
  onToggleLines,
}: {
  view: PlanDatesPreviewView;
  showLines: boolean;
  onToggleLines: () => void;
}) {
  if (view.blocked !== null) {
    return (
      <p className="flex items-start gap-2 rounded-md bg-warning-100 p-2.5 text-sm text-warning-700 dark:text-warning-400">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {view.blocked}
      </p>
    );
  }
  const changedLines = view.lines.filter((l) => l.hoursBefore !== l.hoursAfter);
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-3 gap-2 rounded-md border border-border p-3">
        <Figure
          label="Hours"
          value={`${formatPlanHours(view.hoursBefore)} → ${formatPlanHours(view.hoursAfter)}`}
        />
        <Figure
          label="Months"
          value={
            view.monthsAdded.length === 0 && view.monthsRemoved.length === 0
              ? "Same"
              : `+${view.monthsAdded.length} / −${view.monthsRemoved.length}`
          }
        />
        <Figure
          label={view.marginKind === "contracted" ? "Contracted margin" : "Margin"}
          value={
            view.marginBefore === view.marginAfter
              ? formatPlanPercent(view.marginAfter)
              : `${formatPlanPercent(view.marginBefore)} → ${formatPlanPercent(view.marginAfter)}`
          }
        />
      </dl>

      <p className="text-sm text-muted-foreground">
        New dates:{" "}
        <span className="font-medium text-foreground">
          {range(view.to.startMonth, view.to.endMonth)}
        </span>
        {view.revenueBefore !== view.revenueAfter &&
          ` · revenue ${formatPlanMoney(view.revenueBefore)} → ${formatPlanMoney(view.revenueAfter)}`}
      </p>

      {view.fixedBefore !== null && (
        <Note>Months before {longMonth(view.fixedBefore)} are delivered and stay as they are.</Note>
      )}
      {view.monthsRemoved.length > 0 && view.hoursBefore !== view.hoursAfter && (
        <Note>
          Hours in months that leave the plan ({view.monthsRemoved.map(longMonth).join(", ")}) are
          dropped.
        </Note>
      )}
      {view.rampDropped.length > 0 && (
        <Note>
          Ramp for {view.rampDropped.map(longMonth).join(", ")} has no month to go to and is
          dropped.
        </Note>
      )}
      {view.staffed && (
        <Note>
          This plan is Staffed. Nobody on the Solution changes; the RM is asked to review the Pod
          against the new dates.
        </Note>
      )}

      {view.lines.length > 0 && (
        <div>
          <button
            type="button"
            onClick={onToggleLines}
            className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {showLines ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {changedLines.length === 0
              ? "Every line keeps its hours"
              : `${changedLines.length} line${changedLines.length === 1 ? "" : "s"} change hours`}
          </button>
          {showLines && (
            <table className="mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1 font-medium">Plan Line</th>
                  <th className="py-1 text-right font-medium">Before</th>
                  <th className="py-1 text-right font-medium">After</th>
                </tr>
              </thead>
              <tbody>
                {view.lines.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-1">
                      {l.name}
                      {l.locked && (
                        <span className="ml-1.5 text-xs text-muted-foreground">locked</span>
                      )}
                      {l.split && (
                        <span className="ml-1.5 text-xs text-muted-foreground">split</span>
                      )}
                    </td>
                    <td className="py-1 text-right font-mono">{formatPlanHours(l.hoursBefore)}</td>
                    <td className="py-1 text-right font-mono">{formatPlanHours(l.hoursAfter)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-semibold text-foreground">{value}</dd>
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-sm text-muted-foreground">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
